import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type RelType = {
  id: string;
  campaign_id: string;
  key: string;
  label: string;
  category: "candidate" | "volunteer" | "opponent" | "party" | "organization" | "voter" | "other";
  is_active: boolean;
  sort_order: number;
  created_by?: string | null;
  created_at?: string;
};

const DEFAULTS: Array<Pick<RelType, "key" | "label" | "category" | "sort_order">> = [
  { key: "spouse", label: "Spouse", category: "voter", sort_order: 1 },
  { key: "child", label: "Child", category: "voter", sort_order: 2 },
  { key: "parent", label: "Parent", category: "voter", sort_order: 3 },
  { key: "sibling", label: "Sibling", category: "voter", sort_order: 4 },
  { key: "relative", label: "Relative", category: "voter", sort_order: 5 },
  { key: "friend", label: "Friend", category: "voter", sort_order: 6 },
  { key: "neighbor", label: "Neighbor", category: "voter", sort_order: 7 },
  { key: "coworker", label: "Coworker", category: "voter", sort_order: 8 },
  { key: "classmate", label: "Classmate", category: "voter", sort_order: 9 },
  { key: "church_member", label: "Church Member", category: "voter", sort_order: 10 },
];

const CATEGORIES = ["candidate","volunteer","opponent","party","organization","voter","other"] as const;

export default function SettingsRelationships() {
  const campaignId = useCampaignId();

  const [rows, setRows] = useState<RelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<RelType["category"] | "all">("all");

  // create form
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState<RelType["category"]>("voter");
  const [newSort, setNewSort] = useState<number | "">("");

  // edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKey, setEditKey] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editCategory, setEditCategory] = useState<RelType["category"]>("voter");
  const [editSort, setEditSort] = useState<number | "">("");
  const [editActive, setEditActive] = useState<boolean>(true);

  useEffect(() => {
    if (!campaignId) return;
    void load(campaignId);
  }, [campaignId]);

  async function load(cid: string) {
    setLoading(true);
    setErr(null);
    const { data, error } = await supabase
      .from("campaign_relationship_types")
      .select("*")
      .eq("campaign_id", cid)
      .order("category", { ascending: true })
      .order("sort_order", { ascending: true });
    if (error) setErr(error.message);
    setRows((data ?? []) as RelType[]);
    setLoading(false);
  }

  function toKey(s: string) {
    return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  }

  const filtered = useMemo(() => {
    return filter === "all" ? rows : rows.filter(r => r.category === filter);
  }, [rows, filter]);

  async function create() {
    if (!campaignId) return;
    setErr(null);
    const key = toKey(newKey || newLabel);
    if (!key || !newLabel.trim()) { setErr("Key/Label required."); return; }
    const payload = {
      campaign_id: campaignId,
      key,
      label: newLabel.trim(),
      category: newCategory,
      sort_order: newSort === "" ? 100 : Number(newSort),
      is_active: true,
    };
    const { error } = await supabase.from("campaign_relationship_types").insert([payload]);
    if (error) { setErr(error.message); return; }
    setNewKey(""); setNewLabel(""); setNewCategory("voter"); setNewSort("");
    await load(campaignId);
  }

  function beginEdit(r: RelType) {
    setEditingId(r.id);
    setEditKey(r.key);
    setEditLabel(r.label);
    setEditCategory(r.category);
    setEditSort(r.sort_order);
    setEditActive(r.is_active);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditKey(""); setEditLabel(""); setEditCategory("voter"); setEditSort(""); setEditActive(true);
  }

  async function saveEdit(id: string) {
    if (!campaignId) return;
    setErr(null);
    const payload = {
      key: toKey(editKey || editLabel),
      label: editLabel.trim(),
      category: editCategory,
      sort_order: editSort === "" ? 100 : Number(editSort),
      is_active: !!editActive,
    };
    const { error } = await supabase.from("campaign_relationship_types").update(payload).eq("id", id);
    if (error) { setErr(error.message); return; }
    cancelEdit();
    await load(campaignId);
  }

  async function remove(id: string) {
    if (!campaignId) return;
    if (!window.confirm("Delete this relationship type?")) return;
    const { error } = await supabase.from("campaign_relationship_types").delete().eq("id", id);
    if (error) setErr(error.message);
    await load(campaignId);
  }

  async function seedDefaults() {
    if (!campaignId) return;
    setErr(null);
    const existingKeys = new Set(rows.map(r => r.key.toLowerCase()));
    const toInsert = DEFAULTS.filter(d => !existingKeys.has(d.key.toLowerCase())).map(d => ({
      campaign_id: campaignId,
      key: d.key,
      label: d.label,
      category: d.category,
      sort_order: d.sort_order,
      is_active: true,
    }));
    if (toInsert.length === 0) { window.alert("Defaults already exist."); return; }
    const { error } = await supabase.from("campaign_relationship_types").insert(toInsert);
    if (error) { setErr(error.message); return; }
    await load(campaignId);
    window.alert("Defaults seeded.");
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Relationship Types</h1>
      <p style={{ color: "#555", marginBottom: 16 }}>
        Manage the list of relationship types available in this campaign (stored in <code>campaign_relationship_types</code>).
      </p>

      {!campaignId ? (
        <div>Select a campaign from the top menu to edit relationship types.</div>
      ) : (
        <>
          {/* Create */}
          <section style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>Add Relationship Type</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                placeholder="Key (auto from label if blank)"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, minWidth: 200 }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
              <input
                placeholder="Label (e.g., Spouse)"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, minWidth: 220 }}
              />
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value as RelType["category"])}
                style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                placeholder="Sort order"
                type="number"
                value={newSort}
                onChange={e => setNewSort(e.target.value === "" ? "" : Number(e.target.value))}
                style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, width: 140 }}
              />
              <button
                onClick={create}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #0ea5e9", background: "#0ea5e9", color: "#fff", cursor: "pointer" }}
              >
                Add
              </button>
              <div style={{ flex: 1 }} />
              <label style={{ color: "#444" }}>Filter:</label>
              <select
                value={filter}
                onChange={e => setFilter(e.target.value as typeof filter)}
                style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
              >
                <option value="all">all</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                onClick={seedDefaults}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ccc", background: "#f6f6f6", cursor: "pointer" }}
              >
                Seed voter defaults
              </button>
            </div>
          </section>

          {/* Errors */}
          {err && (
            <div style={{ background: "#ffe8e8", border: "1px solid #f2b0b0", padding: 8, borderRadius: 6, marginBottom: 12, color: "#a40000" }}>
              {err}
            </div>
          )}

          {/* Table */}
          <div style={{ border: "1px solid #e6e6e6", borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#fafafa" }}>
                <tr>
                  <th style={th}>Key</th>
                  <th style={th}>Label</th>
                  <th style={th}>Category</th>
                  <th style={{ ...th, width: 120 }}>Sort</th>
                  <th style={{ ...th, width: 120 }}>Active</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: 16 }}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 16, color: "#666" }}>No relationship types yet.</td></tr>
                ) : (
                  filtered.map(r => {
                    const isEditing = editingId === r.id;
                    return (
                      <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
                        <td style={td}>
                          {isEditing ? (
                            <input value={editKey} onChange={e => setEditKey(e.target.value)} style={inpt} />
                          ) : r.key}
                        </td>
                        <td style={td}>
                          {isEditing ? (
                            <input value={editLabel} onChange={e => setEditLabel(e.target.value)} style={inpt} />
                          ) : r.label}
                        </td>
                        <td style={td}>
                          {isEditing ? (
                            <select value={editCategory} onChange={e => setEditCategory(e.target.value as RelType["category"])} style={inpt}>
                              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : r.category}
                        </td>
                        <td style={{ ...td, width: 120 }}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editSort}
                              onChange={e => setEditSort(e.target.value === "" ? "" : Number(e.target.value))}
                              style={{ ...inpt, width: 100 }}
                            />
                          ) : r.sort_order}
                        </td>
                        <td style={{ ...td, width: 120 }}>
                          {isEditing ? (
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input type="checkbox" checked={editActive} onChange={e => setEditActive(e.target.checked)} /> Active
                            </label>
                          ) : (r.is_active ? "Yes" : "No")}
                        </td>
                        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(r.id)} style={btnPrimary}>Save</button>
                              <button onClick={cancelEdit} style={btn}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => beginEdit(r)} style={btn}>Edit</button>
                              <button onClick={() => remove(r.id)} style={btnDanger}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", fontWeight: 600, fontSize: 13, color: "#333" };
const td: React.CSSProperties = { padding: "10px 12px", fontSize: 14, verticalAlign: "middle" };
const inpt: React.CSSProperties = { padding: 6, border: "1px solid #ddd", borderRadius: 6, width: "100%" };
const btn: React.CSSProperties = { padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, marginLeft: 6, background: "#fff", cursor: "pointer" };
const btnPrimary: React.CSSProperties = { ...btn, background: "#0ea5e9", borderColor: "#0ea5e9", color: "#fff" };
const btnDanger: React.CSSProperties = { ...btn, borderColor: "#f87171", color: "#b91c1c" };

